import { enabled, limit } from "@giftforward/entitlements";
import { DEFAULT_SIGNATURE_TEMPLATE } from "@giftforward/naming";
import {
  createContext,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CAMPAIGN_BUILDER_ID_KEY,
  DEFAULT_EXPIRY_MODE,
  DEFAULT_REMINDER_EMAIL,
  DEFAULT_STAGE3_EMAIL,
  DEFAULT_STAGE3_LANDING,
  DEFAULT_UB_SETTINGS,
  REMINDER_DEFAULTS,
  releaseCampaignBuilderDraft,
} from "@/constants/campaignBuilder";
import { useCampaignDraftPersistence } from "@/hooks/useCampaignDraftPersistence";
import { useDefaultCauseSelection } from "@/hooks/useCharityCauses";
import { useFundingWallets } from "@/hooks/useFundingWallets";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import type {
  ActivationMode,
  CampaignBuilderCtx,
  CropTarget,
  ExpiryMode,
  LandingSettings,
  ModalTrigger,
  NotificationMode,
  PlanUpsellContext,
  Recipient,
  ReminderEmailState,
  ScheduleMode,
  Stage3EmailState,
  UnboxingSettings,
  ValidationIssue,
} from "@/interfaces/CampaignBuilder";
import type {
  BuilderCampaignPayload,
  CampaignDraftDetail,
  FeedEdits,
} from "@/interfaces/campaignDraft";
import type { CauseSelection } from "@/interfaces/charity";
import type { Product } from "@/interfaces/product";
import {
  adoptDefaultCauses,
  type CauseChoice,
  chooseCauses,
  hydrateCauseChoice,
  UNDECIDED_CAUSES,
} from "@/lib/campaignBuilderCauses";
import {
  buildCreateReminderDefaults,
  buildStep1Payload,
  buildStep2Payload,
  buildStep3Payload,
  buildStep4Payload,
  buildTitlePayload,
} from "@/lib/campaignBuilderPayload";
import type { CampaignStatus, CanvasTab, ConfigTab } from "@/types/types";
import { isRecipientsLocked } from "@/utils/campaign-gating-helper";
import {
  campaignNameModalMode,
  isValidCampaignName,
  normalizeCampaignName,
} from "@/utils/campaignName-helper";
import { hasLandingVideo } from "@/utils/landing-video-helper";
import { defaultCustomExpiryDate, defaultScheduleDate } from "@/utils/scheduleDate-helper";

export { DEFAULT_REMINDER_EMAIL, DEFAULT_UB_SETTINGS } from "@/constants/campaignBuilder";
// Re-export the builder types + public defaults from their homes so existing
// `@/context/CampaignBuilderContext` imports keep working.
export type {
  ActivationMode,
  BrandingFields,
  CropTarget,
  ExpiryMode,
  GfgModalContext,
  LandingSettings,
  ModalTrigger,
  NotificationMode,
  PlanUpsellContext,
  Recipient,
  ReminderEmailState,
  ScheduleMode,
  Stage3EmailState,
  UnboxingSettings,
  ValidationIssue,
} from "@/interfaces/CampaignBuilder";

const CampaignBuilderContext = createContext<CampaignBuilderCtx | null>(null);

// Stable empty views: BudgetPerRecipient memoizes on these, so "nothing" keeps one identity.
const NO_GRIDS: Record<string, Product[]> = {};
const NO_FEED_EDITS: FeedEdits = {};

export function CampaignBuilderProvider({ children }: { children: ReactNode }) {
  // ── Step 1 — collection ────────────────────────────────────────────────────
  const [selectedBudget, setSelectedBudget] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState("US");
  // Per-tier, per-country edited grids — neither the tier pills nor the country dropdown reset
  // anything: each tier keeps its own grids for the session. `savedGiftsByCountry` and the exposed
  // `savedGifts`/`setSavedGifts`/`setSavedGiftsByCountry` are views over the CURRENT tier only.
  const [giftsByBudget, setGiftsByBudget] = useState<Record<number, Record<string, Product[]>>>({});
  const savedGiftsByCountry =
    (selectedBudget === null ? undefined : giftsByBudget[selectedBudget]) ?? NO_GRIDS;
  const setSavedGiftsByCountry = useCallback(
    (action: SetStateAction<Record<string, Product[]>>) => {
      if (selectedBudget === null) return;
      setGiftsByBudget((prev) => {
        const current = prev[selectedBudget] ?? NO_GRIDS;
        const next = typeof action === "function" ? action(current) : action;
        // Unchanged ⇒ same store object, or BudgetPerRecipient's eager-hydrate effect loops.
        return next === current ? prev : { ...prev, [selectedBudget]: next };
      });
    },
    [selectedBudget],
  );
  // Hydrated (edit mode): per-country diffs + added-product data. BudgetPerRecipient reconstructs
  // each country's grid from these + the budget collections it fetches (see resolveCountryFeed).
  // The diffs were saved against ONE tier, so only that tier is handed them.
  const [loadedFeedEdits, setLoadedFeedEdits] = useState<FeedEdits>({});
  const [hydratedBudget, setHydratedBudget] = useState<number | null>(null);
  const hydratedFeedEdits = selectedBudget === hydratedBudget ? loadedFeedEdits : NO_FEED_EDITS;
  /**
   * Has the CURRENT tier's gift grid actually been CHANGED, as opposed to merely looked at?
   *
   * `savedGiftsByCountry` is seeded with the DEFAULT composed grid the moment Step 1 renders
   * (BudgetPerRecipient stores `countryFeed` so the page has something to draw), so it cannot
   * answer this — and sending that default as `feeds` told the server the client had edited a
   * collection. On a plan that does not include collection editing the whole save was then
   * refused, for something the client never did: picking a budget was enough to be told
   * "Your plan doesn't include editing gift collections."
   *
   * Tracked per tier. Set only where a real change is committed — `saveFeed` — and, for the
   * loaded tier, when a loaded campaign already carries stored edits. A tier switch keeps it.
   */
  const [editedBudgets, setEditedBudgets] = useState<ReadonlySet<number>>(() => new Set());
  const feedEdited = selectedBudget !== null && editedBudgets.has(selectedBudget);
  const [hydratedAddedProducts, setHydratedAddedProducts] = useState<Product[]>([]);

  const savedGifts = savedGiftsByCountry[selectedCountry] ?? null;
  const setSavedGifts = useCallback(
    (v: Product[] | null) => {
      setSavedGiftsByCountry((prev) => {
        if (v === null) {
          if (!(selectedCountry in prev)) return prev;
          const next = { ...prev };
          delete next[selectedCountry];
          return next;
        }
        return { ...prev, [selectedCountry]: v };
      });
    },
    [selectedCountry, setSavedGiftsByCountry],
  );

  function handleSetSelectedBudget(v: number | null) {
    // A tier switch is a VIEW switch: each tier keeps its own grids + edited flag, so coming back
    // to a tier shows its collection exactly as the user left it.
    setSelectedBudget(v);
  }

  // ── Campaign status (edit-mode gating) ──────────────────────────────────────
  /**
   * The loaded campaign's status, hydrated from the server detail in `applyDetail`. `null` is
   * CREATE mode: no campaign exists yet, so there is nothing to gate — every lock in
   * `campaign-gating-helper` answers `false` for `null`, which is what keeps campaign creation
   * byte-for-byte unchanged. Never derived from a card, sessionStorage or a cache: only the
   * server's `derivedStatus` at load time may set it.
   */
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus | null>(null);

  // ── GFG-413 campaign name ───────────────────────────────────────────────────
  const [campaignName, setCampaignName] = useState("");
  const [isRenamingCampaign, setIsRenamingCampaign] = useState(false);
  // New-vs-existing is read ONCE at mount: an existing draft hydrates asynchronously, so deciding
  // from `campaignId` would flash the create modal while it loads.
  const [isNewCampaign] = useState(() => !sessionStorage.getItem(CAMPAIGN_BUILDER_ID_KEY));
  const openCampaignRename = useCallback(() => setIsRenamingCampaign(true), []);
  const closeCampaignRename = useCallback(() => setIsRenamingCampaign(false), []);

  // ── Modal ───────────────────────────────────────────────────────────────────
  const [modal, setModal] = useState<ModalTrigger | null>(null);
  const openModal = useCallback((trigger: ModalTrigger) => setModal(trigger), []);
  const closeModal = useCallback(() => setModal(null), []);
  const [planModal, setPlanModal] = useState<{ context: PlanUpsellContext | null } | null>(null);
  const openPlanModal = useCallback(
    (context?: PlanUpsellContext) => setPlanModal({ context: context ?? null }),
    [],
  );
  const closePlanModal = useCallback(() => setPlanModal(null), []);

  // ── Step 2 — recipients + delivery ───────────────────────────────────────────
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [notificationMode, setNotificationMode] = useState<NotificationMode>("gfg");
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>("asap");
  const [scheduleDate, setScheduleDate] = useState(() => defaultScheduleDate());
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleTz, setScheduleTz] = useState("ET");
  // Default is the 60-day preset (the recommended option — GFG-438).
  const [expiryMode, setExpiryMode] = useState<ExpiryMode>(DEFAULT_EXPIRY_MODE);
  const [customExpiryDate, setCustomExpiryDate] = useState(() => defaultCustomExpiryDate());
  // ── Step 2 — manual gift links ("You'll send gift links manually") ────────────
  // Distribution path: null until the sender answers (Continue waits), then false → anonymous links
  // by quantity (Path A), true → one link per uploaded recipient (Path B, reuses the gfg list).
  const [linkToList, setLinkToList] = useState<boolean | null>(null);
  const [linkQuantity, setLinkQuantity] = useState(25);
  const [senderFirstName, setSenderFirstName] = useState("");
  const [senderLastName, setSenderLastName] = useState("");
  const [senderCompany, setSenderCompany] = useState("");
  // Universal Link signature (unboxing @Signature), prefilled with the sender tokens; only sent for
  // universal (cleared otherwise, so Manual Path A keeps its no-signature behavior).
  const [senderSignature, setSenderSignature] = useState(DEFAULT_SIGNATURE_TEMPLATE);
  const [activationMode, setActivationMode] = useState<ActivationMode>("immediate");
  const [activationDate, setActivationDate] = useState(() => defaultScheduleDate());
  const [activationTime, setActivationTime] = useState("09:00");
  const [activationTz, setActivationTz] = useState("ET");
  // ── Step 2 — universal link ("Generate a single universal link") ───────────────
  // Accept the multiple-use terms → the ONE public /u/ link is minted server-side on the save that
  // `acceptUniversal` triggers; its suffix comes back as `draft.universalToken`. `accepted` is session
  // state that reveals the link panel + gates Continue; the link + mode persist on the row. Universal
  // reuses the campaign-level sender fields + quantity stepper (same state as manual Path A).
  const [universalAccepted, setUniversalAccepted] = useState(false);
  const [universalAcceptedAt, setUniversalAcceptedAt] = useState<string | null>(null);
  // Step 2 gate. gfg is always complete here by design: an incomplete recipient row is surfaced as a
  // NON-BLOCKING warning in the Recipients table (flagged cells + a header count) and enforced at
  // launch, so the sender can keep editing and come back rather than being trapped on this step.
  //   Manual  → the distribution path must be answered (Continue waits while linkToList is null), then
  //             Path A (false) needs a campaign-level sender first name + company (they feed the
  //             unboxing @SenderFirstName / @SenderCompany tokens; quantity snaps to ≥1 so it isn't
  //             gated here, launch enforces qty > 0); Path B (true) reuses the gfg list (rows warned,
  //             not gated, the same way).
  //   Universal → terms accepted (the link is generated) + the same campaign-level sender first+company.
  const step2Complete =
    notificationMode === "gfg"
      ? true
      : notificationMode === "universal"
        ? universalAccepted && senderFirstName.trim() !== "" && senderCompany.trim() !== ""
        : linkToList === null
          ? false
          : linkToList
            ? true
            : senderFirstName.trim() !== "" && senderCompany.trim() !== "";

  // ── Step 3 — design ──────────────────────────────────────────────────────────
  const [stage3Email, setStage3EmailState] = useState<Stage3EmailState>(DEFAULT_STAGE3_EMAIL);
  function setStage3Email(v: Partial<Stage3EmailState>) {
    setStage3EmailState((prev) => ({ ...prev, ...v }));
  }
  const [stage3Unboxing, setStage3UnboxingState] = useState<UnboxingSettings>(DEFAULT_UB_SETTINGS);
  function setStage3Unboxing(v: Partial<UnboxingSettings>) {
    setStage3UnboxingState((prev) => ({ ...prev, ...v }));
  }
  const [stage3Landing, setStage3LandingState] = useState<LandingSettings>(DEFAULT_STAGE3_LANDING);
  function setStage3Landing(v: Partial<LandingSettings>) {
    setStage3LandingState((prev) => ({ ...prev, ...v }));
  }

  // Step 3 is complete once no enabled feature is left without its required input: the
  // gift-message toggle needs a message, the landing Video toggle needs a video, and each enabled
  // logo (email + landing) needs an uploaded image. Extend this predicate as more step-3
  // requirements are added; the per-issue detail (message + Fix) shown in the validation toast
  // lives in CampaignBuilderNav.
  // The landing logo, when synced, IS the email logo (mirrors it) rather than the landing's own
  // `logoUrl`; only an unlinked landing has its own image. Validate against the resolved value so a
  // synced landing counts as complete off the email logo. Mirror this in CampaignBuilderNav.
  const resolvedLandingLogoUrl = stage3Landing.logoSynced
    ? stage3Email.logoUrl
    : stage3Landing.logoUrl;
  const step3Complete =
    (!stage3Unboxing.showGiftMessage || stage3Unboxing.giftMessageText.trim() !== "") &&
    // The landing Video toggle is satisfied by a custom link OR the default video (GiftVideoModal
    // falls back to DEFAULT_LANDING_VIDEO); it only blocks when neither exists.
    (!stage3Landing.showVideo || hasLandingVideo(stage3Landing.videoUrl)) &&
    // Self-send (manual + universal) has no GfG email, so its logo requirement is moot (Email tab locked).
    (notificationMode !== "gfg" || !stage3Email.logoEnabled || stage3Email.logoUrl.trim() !== "") &&
    (!stage3Landing.logoEnabled || resolvedLandingLogoUrl.trim() !== "");

  const [designActiveTab, setDesignActiveTab] = useState<CanvasTab>("email");
  const [designDrawerOpen, setDesignDrawerOpen] = useState(false);
  const [fullscreenPreviewOpen, setFullscreenPreviewOpen] = useState(false);
  // Email + Landing customize drawers' sub-tab (Message / Branding). In context so a
  // step-3 validation Fix can route straight to the sub-tab holding the incomplete field.
  const [emailConfigTab, setEmailConfigTab] = useState<ConfigTab>("message");
  const [landingConfigTab, setLandingConfigTab] = useState<ConfigTab>("message");

  // Reminders (part of the Step 2 save-group; edited from the Recipients step + reminder sub-page).
  const [reminderCount, setReminderCount] = useState(4);
  /**
   * What the PLAN permits, applied to the count the payload actually carries.
   *
   * The default is 4, and GiftReminders correctly locks its accordion when the plan allows none —
   * but locking the control does not change the state behind it, so the payload still offered four
   * reminders nobody had asked for and the save was refused with 402. A client on Starter met a
   * plan refusal for a screen they could not even open.
   *
   * Clamped HERE rather than in the reminders UI because this is the only place every save passes
   * through. `null` is unlimited, not zero — but a missing `builderEnt` (still loading) is
   * UNKNOWN, and also yields `null` here; the draft create checks `builderEnt` itself for that.
   */
  const { ent: builderEnt } = useMyEntitlements();
  const reminderCap = builderEnt ? limit(builderEnt, "campaign_reminders", "max") : null;

  // ── Funding wallet (multi-group plans) ──────────────────────────────────────
  const canChooseWallet = !!builderEnt && enabled(builderEnt, "wallet", "multi_group");
  const {
    data: fundingWalletsData,
    isLoading: fundingWalletsLoading,
    isError: fundingWalletsError,
    refetch: refetchFundingWallets,
  } = useFundingWallets(canChooseWallet);
  const fundingWallets = fundingWalletsData ?? [];
  const [fundingGroupId, setFundingGroupId] = useState<number | null>(null);
  const permittedReminderCount =
    reminderCap === null ? reminderCount : Math.min(reminderCount, reminderCap);
  const [reminderEmails, setReminderEmailsState] = useState<Record<number, ReminderEmailState>>({
    0: { ...REMINDER_DEFAULTS[0]! },
    1: { ...REMINDER_DEFAULTS[1]! },
    2: { ...REMINDER_DEFAULTS[2]! },
    3: { ...REMINDER_DEFAULTS[3]! },
  });
  function setReminderEmail(index: number, v: Partial<ReminderEmailState>) {
    setReminderEmailsState((prev) => ({
      ...prev,
      [index]: { ...(prev[index] ?? DEFAULT_REMINDER_EMAIL), ...v },
    }));
  }

  // ── Builder UI state (persists across step navigation) ───────────────────────
  const [recipientsAccordions, setRecipientsAccordions] = useState<Set<string>>(new Set());
  const toggleRecipientsAccordion = useCallback((id: string) => {
    setRecipientsAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const openRecipientsAccordion = useCallback((id: string) => {
    setRecipientsAccordions((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);
  const [remindersOpen, setRemindersOpen] = useState(false);

  const [cropDialogTarget, setCropDialogTarget] = useState<CropTarget | null>(null);
  const openCropDialog = useCallback((target: CropTarget) => setCropDialogTarget(target), []);
  const closeCropDialog = useCallback(() => setCropDialogTarget(null), []);

  const [sendTestOpen, setSendTestOpen] = useState(false);
  const openSendTest = useCallback(() => setSendTestOpen(true), []);
  const closeSendTest = useCallback(() => setSendTestOpen(false), []);

  const [toastMessage, setToastMessage] = useState("");
  const showToast = useCallback((message: string) => setToastMessage(message), []);
  const clearToast = useCallback(() => setToastMessage(""), []);

  // ── Step 4 — review & checkout ───────────────────────────────────────────────
  // CauseSelector enforces its own MAX_CAUSES cap and re-derives even shares on every change,
  // so this is a plain store of what it emits.
  const [causeChoice, setCauseChoice] = useState<CauseChoice>(UNDECIDED_CAUSES);
  // Copied out of the (readonly) choice so consumers keep the mutable CauseSelection[] the ctx
  // type promises, without handing them the array the choice holds.
  const selectedCauses = useMemo(() => [...causeChoice.causes], [causeChoice.causes]);
  const setSelectedCauses = useCallback(
    (v: CauseSelection[]) => setCauseChoice(chooseCauses(v)),
    [],
  );
  const { data: platformDefault } = useDefaultCauseSelection();
  const [rolloverEnabled, setRolloverEnabled] = useState(false);
  const [applyAllCredits, setApplyAllCredits] = useState(true);
  const [payAsYouGive, setPayAsYouGive] = useState(false);
  const [postpaid, setPostpaid] = useState(false);
  // TODO(step4): POST the assembled campaign once launch is wired; `saveAllDirty` (below) already
  // persists any pending draft changes before the launch mutation runs.
  /**
   * The launch succeeded, so this campaign is no longer a draft the builder may edit.
   *
   * Releasing the stash is the whole job: without it the id survived in sessionStorage and every
   * later "+" reopened a LAUNCHED campaign, whose step autosaves then rewrote a campaign that
   * already had a budget and commitments against it.
   */
  function submitCampaign() {
    releaseCampaignBuilderDraft();
    draft.setCampaignId("");
    // Dropping the id puts the builder back in CREATE mode, so the status must go with it —
    // `campaignStatus === null` and "no campaign loaded" are the same state by definition.
    setCampaignStatus(null);
  }

  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const showValidation = useCallback(
    (issues: ValidationIssue[]) => setValidationIssues(issues),
    [],
  );
  const clearValidation = useCallback(() => setValidationIssues([]), []);

  // ── Save-group payloads (each a slice the persistence engine dirty-tracks) ────
  // CURRENT tier only — another tier's grids would be diffed server-side against the wrong tier.
  // Per-country grids → feeds payload (country → ordered product_ids). Empty ⇒ Step 1 was never
  // engaged this session, so buildStep1Payload omits `feeds` and the server leaves feed_edits
  // untouched (never wiped by an unrelated step save).
  function currentFeeds(): Record<string, string[]> {
    const feeds: Record<string, string[]> = {};
    for (const [country, grid] of Object.entries(savedGiftsByCountry)) {
      feeds[country] = grid.map((g) => g.id);
    }
    return feeds;
  }

  // Group 1's dirty key is its META only (budget / country / international-unlock). The product
  // feed is committed + persisted eagerly by the feed Save button, so it never sits "unsaved" at a
  // step boundary except an unsaved Edit Collection grid, which saveStep1OnLeave saves; so
  // Continue/jump only needs to catch budget/country/plan changes.
  const step1MetaKey = () => JSON.stringify({ budget: selectedBudget, country: selectedCountry });

  // Step-2 payload from an explicit recipients list — used both for the current-state group
  // payload and for the immediate add/import saves (which pass the post-change list).
  function step2Payload(
    recipientsArg: Recipient[],
    scheduleModeArg: ScheduleMode = scheduleMode,
  ): BuilderCampaignPayload {
    return buildStep2Payload(
      {
        recipients: recipientsArg,
        notificationMode,
        scheduleMode: scheduleModeArg,
        scheduleDate,
        scheduleTime,
        scheduleTz,
        expiryMode,
        customExpiryDate,
        linkToList,
        linkQuantity,
        senderFirstName,
        senderLastName,
        senderCompany,
        // Maps to the shared `campaigns.signature`, so send it ONLY for universal — for gfg that
        // column holds the EMAIL signature (from the design step), and sending "" here would wipe it.
        senderSignature: notificationMode === "universal" ? senderSignature : undefined,
        activationMode,
        activationDate,
        activationTime,
        activationTz,
        reminderCount: permittedReminderCount,
        reminderEmails,
      },
      // Once past draft, the recipient list and reminders are frozen (server refuses them on
      // presence) — send only the post-schedule-editable delivery fields so the save isn't a 409.
      { editableOnly: isRecipientsLocked(campaignStatus) },
    );
  }

  /** The wire payload for one save-group, from current builder state. */
  function buildGroupPayload(step: number): BuilderCampaignPayload {
    switch (step) {
      case 1:
        // `feeds` ONLY when the grid was really changed. Omitted, the server leaves the stored
        // feed alone — which is the correct outcome for a step save that did not touch it.
        return buildStep1Payload(selectedBudget, feedEdited ? currentFeeds() : {}, selectedCountry);
      case 2:
        return step2Payload(recipients);
      case 3:
        return buildStep3Payload({
          email: stage3Email,
          unboxing: stage3Unboxing,
          landing: stage3Landing,
          // Self-send has no email signature; its design save must not overwrite `campaigns.signature`,
          // which for a universal campaign holds the sender signature (see serializer/SenderInfoFields).
          omitEmailSignature: notificationMode !== "gfg",
        });
      case 4:
        return buildStep4Payload({ rolloverEnabled, selectedCauses, payAsYouGive, postpaid });
      default:
        return {};
    }
  }

  /** Strip undefined so partial setters never overwrite a value with `undefined`. */
  function defined<T extends object>(obj: T): Partial<T> {
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
  }

  // Apply a loaded draft's columns onto step state (the persistence hook owns id/token/reseed).
  function applyDetail(d: CampaignDraftDetail) {
    // Status first — the step-1/step-2 locks read it. `derivedStatus` (not the legacy `status`
    // varchar), and `?? null` so a server build that predates the field degrades to CREATE-mode
    // behaviour (nothing locked) instead of `undefined` leaking into the lock helpers.
    setCampaignStatus(d.derivedStatus ?? null);
    setCampaignName(d.title ?? "");
    setFundingGroupId(d.groupId ?? null);
    if (d.budget != null) setSelectedBudget(d.budget);
    if (d.country) setSelectedCountry(d.country);
    // Per-country feed edits: drop every tier's in-memory grids and stash the stored diffs (tagged
    // with their tier) + added-product data. BudgetPerRecipient reconstructs each country's grid
    // (compose + its diff) on load.
    setGiftsByBudget({});
    setLoadedFeedEdits(d.feedEdits ?? {});
    setHydratedBudget(d.budget ?? null);
    // Already-stored edits are edits of the loaded tier: a later Step 1 save of that tier must not
    // drop them from the payload.
    setEditedBudgets(
      d.budget != null && Object.keys(d.feedEdits ?? {}).length > 0
        ? new Set([d.budget])
        : new Set(),
    );
    setHydratedAddedProducts(d.addedProducts ?? []);
    if (d.recipients) {
      setRecipients(
        d.recipients.map((r) => ({
          id: crypto.randomUUID(),
          firstName: r.firstName,
          lastName: r.lastName ?? "",
          email: r.email,
          toCompany: r.toCompany,
          recipientCountry: r.recipientCountry,
          senderFirst: r.senderFirst ?? "",
          senderLast: r.senderLast ?? "",
          senderCompany: r.senderCompany ?? "",
          fromEmail: r.fromEmail,
          senderSignature: r.senderSignature,
          sendDate: r.sendDate,
          sendTime: r.sendTime,
        })),
      );
    }
    if (d.notificationMode) setNotificationMode(d.notificationMode);
    if (d.scheduleMode) setScheduleMode(d.scheduleMode);
    // A "certain" schedule saved with no date (GFG-378) must read as missing, not as the "tomorrow"
    // default: Step 4 then blocks checkout instead of showing a date the campaign does not have.
    if (d.scheduleDate) setScheduleDate(d.scheduleDate);
    else if (d.scheduleMode === "certain") setScheduleDate("");
    if (d.scheduleTime) setScheduleTime(d.scheduleTime);
    if (d.scheduleTz) setScheduleTz(d.scheduleTz);
    if (d.expiryMode) setExpiryMode(d.expiryMode);
    if (d.customExpiryDate) setCustomExpiryDate(d.customExpiryDate);
    // Manual gift links (activation is read back off the scheduled_* columns server-side). Assigned
    // UNCONDITIONALLY with a default — never guarded — because hydrate can load a DIFFERENT campaign
    // into the same open builder (see the reminder reset below), and `buildStep2Payload` sends these
    // regardless of mode. Guarding on `d.x != null` would let a prior MANUAL draft's values survive a
    // load of a GFG draft (which carries null here) and then get written back onto the gfg row. The
    // `??` defaults mirror the initial state (useState above).
    // linkToList keeps null when absent (the unanswered state) rather than defaulting a path the
    // sender never chose.
    setLinkToList(d.linkToList ?? null);
    setLinkQuantity(d.linkQuantity ?? 25);
    setSenderFirstName(d.senderFirstName ?? "");
    setSenderLastName(d.senderLastName ?? "");
    setSenderCompany(d.senderCompany ?? "");
    setSenderSignature(d.senderSignature?.trim() ? d.senderSignature : DEFAULT_SIGNATURE_TEMPLATE);
    setActivationMode(d.activationMode ?? "immediate");
    setActivationDate(d.activationDate ?? defaultScheduleDate());
    setActivationTime(d.activationTime ?? "09:00");
    setActivationTz(d.activationTz ?? "ET");
    // Universal link: the /u/ link is minted ONLY on accept (server-side), so its presence is a reliable
    // record that the terms were accepted — a reopened accepted draft shows the link panel, an
    // unaccepted one still shows the terms gate. Unconditional (same reasoning as the manual fields
    // above) so loading a non-universal campaign clears a prior universal's accepted state. acceptedAt
    // is session metadata — reset on load, re-stamped on an in-session accept.
    setUniversalAccepted(d.universalToken != null);
    setUniversalAcceptedAt(null);
    if (d.email) setStage3Email(defined(d.email));
    if (d.unboxing) setStage3Unboxing(defined(d.unboxing));
    if (d.landing) setStage3Landing(defined(d.landing) as Partial<LandingSettings>);
    if (d.reminderCount != null) setReminderCount(d.reminderCount);
    // Reset any touched reminder-date override BEFORE applying this campaign's own content —
    // hydrate can load a DIFFERENT campaign into the same open builder (the dev testing panel's
    // load-by-id, or starting a new draft after editing one), and a Pro sender's unsaved pick for
    // the PREVIOUS campaign must never leak into this one as a phantom override.
    for (let i = 0; i < 4; i++) setReminderEmail(i, { sendDate: undefined, sendTime: undefined });
    for (const rem of d.reminders ?? []) {
      setReminderEmail(rem.order - 1, {
        subject: rem.subject,
        messageHtml: rem.content,
        showLogo: rem.showLogo,
        showBanner: rem.showBanner,
      });
    }
    // Step 4 — rollover + charity causes (checkout fields are launch-only, never hydrated).
    if (d.rollover !== undefined) setRolloverEnabled(d.rollover);
    setCauseChoice(hydrateCauseChoice(d.causes));
  }

  // ── Persistence engine (create/PATCH, dirty snapshots, load/delete lifecycle) ─
  /**
   * A slot the server just cancelled stops being the sender's pick.
   *
   * `reminderEmails` outlives step navigation, so without this Step 2 goes on rendering — and
   * validating — a date the campaign no longer has: the sender shortens the expiry, the save
   * drops reminder 4, and they come back to reminder 4 still showing its old date under a red
   * "must be before the expiration date". Clearing the pick returns the slot to untouched, which
   * is what the campaign now actually holds.
   */
  function forgetCancelledReminders(orders: number[]) {
    setReminderEmailsState((prev) => {
      const next = { ...prev };
      for (const order of orders) {
        const idx = order - 1;
        const current = next[idx];
        if (!current) continue;
        next[idx] = { ...current, sendDate: undefined, sendTime: undefined };
      }
      return next;
    });
  }

  const draft = useCampaignDraftPersistence({
    buildGroupPayload,
    step1MetaKey,
    applyDetail,
    onRemindersCancelled: forgetCancelledReminders,
    showToast,
    // `builderEnt` absent = entitlements still loading: the cap is unknown, so the create carries
    // no reminders rather than an unclamped 4 (see buildCreateReminderDefaults). The name rides
    // along so the draft is not born as the server's "Untitled Campaign".
    buildCreateDefaults: () => ({
      ...buildCreateReminderDefaults(!!builderEnt, permittedReminderCount, reminderEmails),
      ...buildTitlePayload(campaignName),
      ...(canChooseWallet && fundingGroupId !== null ? { groupId: fundingGroupId } : {}),
    }),
  });

  // Computed after `draft`: a stashed id that failed to load hydrated nothing, so the next save
  // creates a new draft — it gets the create modal like any new campaign.
  const campaignNameModal = campaignNameModalMode({
    isNewCampaign: isNewCampaign || draft.initialLoadFailed,
    campaignName,
    isRenaming: isRenamingCampaign,
  });

  /**
   * Name modal submit. With a draft, PATCHes `{ title }` ONLY (persistGroup/saveStepIfDirty would
   * also send budget/feeds) and changes nothing if that fails — `persist` already toasted. Without
   * one (the create needs a budget) the name stays local and rides on the create defaults above.
   */
  async function commitCampaignName(name: string): Promise<boolean> {
    const next = normalizeCampaignName(name);
    if (!isValidCampaignName(next)) return false;
    if (next !== campaignName) {
      if (draft.hasDraft() && (await draft.persist(buildTitlePayload(next))) === null) return false;
      setCampaignName(next);
    }
    setIsRenamingCampaign(false);
    return true;
  }

  /** Same shape as commitCampaignName: PATCH `{ groupId }` with a draft, else ride on the create. */
  async function chooseFundingWallet(groupId: number): Promise<boolean> {
    if (groupId === fundingGroupId) return true;
    if (draft.hasDraft() && (await draft.persist({ groupId })) === null) return false;
    setFundingGroupId(groupId);
    return true;
  }

  // A caller with one wallet has nothing to decide: preselect it (the picker still shows it).
  const onlyWalletId = fundingWallets.length === 1 ? (fundingWallets[0]?.groupId ?? null) : null;
  useEffect(() => {
    if (onlyWalletId !== null && fundingGroupId === null) setFundingGroupId(onlyWalletId);
  }, [onlyWalletId, fundingGroupId]);

  // hydrate() batches applyDetail + the snapshot-seed bump into ONE commit, so the seed reads the
  // pre-adopt state and the adopted default stays an unsaved change for saveStepIfDirty(4).
  useEffect(() => {
    if (causeChoice.decided) return;
    setCauseChoice((prev) => adoptDefaultCauses(prev, platformDefault));
  }, [platformDefault, causeChoice.decided]);

  // Step 1 feed Save (the feed Save button + the unsaved-changes modal): persist Step 1, then
  // commit the active country's grid to context ONLY if the write landed (a rejected grid must not
  // pose as saved). `feeds` is built from the NEXT grid so the save doesn't read the pre-update
  // state.
  async function saveFeed(grid: Product[]): Promise<boolean> {
    const nextByCountry = { ...savedGiftsByCountry, [selectedCountry]: grid };
    const feeds: Record<string, string[]> = {};
    for (const [c, g] of Object.entries(nextByCountry)) feeds[c] = g.map((p) => p.id);
    const payload = buildStep1Payload(selectedBudget, feeds, selectedCountry);
    // Returned, not voided: a caller that is about to DISCARD this grid (switching tier) has to
    // know the write landed first, or a failed save silently destroys the edit.
    const saved = await draft.persistGroup(1, payload, step1MetaKey());
    if (!saved) return false;
    setSavedGifts(grid);
    if (selectedBudget !== null) setEditedBudgets((prev) => new Set(prev).add(selectedBudget));
    return true;
  }

  // Edit Collection's unsaved grid. A ref, not state: it changes on every drag and must not
  // re-render the provider. BudgetPerRecipient reports it; Step 1 leave reads it.
  const pendingFeedRef = useRef<Product[] | null>(null);
  const setPendingFeed = useCallback((grid: Product[] | null) => {
    pendingFeedRef.current = grid;
  }, []);

  // Leaving Step 1: an unsaved Edit Collection grid is saved via ONE feeds PATCH (never also a
  // meta-only PATCH — landing last, it would clear the just-saved feed edits) and AWAITED: the
  // ref is cleared only on success, so a failed save keeps the grid for retry/discard (resolves
  // false too if the grid changed during the save). No pending grid → the usual background META
  // flush, resolving true at once so leaving is never delayed.
  async function saveStep1OnLeave(): Promise<boolean> {
    const pending = pendingFeedRef.current;
    if (pending === null) {
      void draft.saveStepIfDirty(1);
      return true;
    }
    const saved = await saveFeed(pending);
    // The user may edit the grid while the PATCH is in flight: clear only the grid that was
    // saved, and report true only when nothing newer is pending (else the nav's failure branch
    // re-offers it).
    if (saved && pendingFeedRef.current === pending) pendingFeedRef.current = null;
    return saved && pendingFeedRef.current === null;
  }

  // Step 2 recipient mutators — context + immediate save, built from the post-change list.
  function addRecipient(r: Recipient) {
    const next = [...recipients, r];
    setRecipients(next);
    void draft.persistStep2(step2Payload(next));
  }
  // `nextScheduleMode` lands in the same save: setScheduleMode alone would leave this payload on the
  // previous mode, which a file's dates under "Schedule for later" would then contradict.
  function importRecipients(rs: Recipient[], nextScheduleMode?: ScheduleMode) {
    if (rs.length === 0) return;
    const next = [...recipients, ...rs];
    setRecipients(next);
    if (nextScheduleMode) setScheduleMode(nextScheduleMode);
    void draft.persistStep2(step2Payload(next, nextScheduleMode));
  }

  // "ACCEPT AND GENERATE LINK" (Step 2, universal). Reveal the link panel + gate state, then force a
  // The ONLY path that mints the public /u/ link: a Step-2 save carrying `acceptUniversalTerms` tells
  // the server to mint + return the suffix (draft.universalToken, surfaced reactively). Regular saves
  // and GETs only read it, so an unaccepted draft has no link (the terms gate stays up on reopen).
  // In-flight guard: ignore a second click while the accept save is still running, so a double-click
  // can't fire two mint requests (there's no DB unique constraint on the link).
  const acceptingUniversalRef = useRef(false);
  function acceptUniversal() {
    if (acceptingUniversalRef.current || universalAccepted) return;
    acceptingUniversalRef.current = true;
    setUniversalAccepted(true);
    setUniversalAcceptedAt(new Date().toISOString());
    void draft
      .persistStep2({ ...step2Payload(recipients), acceptUniversalTerms: true })
      .finally(() => {
        acceptingUniversalRef.current = false;
      });
  }

  return (
    <CampaignBuilderContext.Provider
      value={{
        // Step 1
        selectedBudget,
        setSelectedBudget: handleSetSelectedBudget,
        selectedCountry,
        setSelectedCountry,
        savedGifts,
        setSavedGifts,
        setSavedGiftsByCountry,
        hydratedFeedEdits,
        hydratedAddedProducts,
        step1Complete: selectedBudget !== null,
        saveFeed,
        setPendingFeed,
        saveStep1OnLeave,
        // Save API (persistence engine)
        isStepDirty: draft.isStepDirty,
        saveStepIfDirty: draft.saveStepIfDirty,
        saveAllDirty: draft.saveAllDirty,
        commitStep1: draft.commitStep1,
        commitStep2: draft.commitStep2,
        commitStep3: draft.commitStep3,
        commitAll: draft.commitAll,
        loadDraft: draft.loadDraft,
        loadCampaign: draft.loadCampaign,
        deleteDraft: draft.deleteDraft,
        editDenied: draft.editDenied,
        campaignId: draft.campaignId,
        setCampaignId: draft.setCampaignId,
        mockToken: draft.mockToken,
        // Modal
        modal,
        openModal,
        closeModal,
        planModal,
        openPlanModal,
        closePlanModal,
        campaignStatus,
        // GFG-413 campaign name
        campaignName,
        campaignNameModal,
        openCampaignRename,
        closeCampaignRename,
        commitCampaignName,
        // Funding wallet
        canChooseWallet,
        fundingWallets,
        fundingWalletsLoading,
        fundingWalletsError,
        reloadFundingWallets: () => void refetchFundingWallets(),
        fundingGroupId: canChooseWallet ? fundingGroupId : null,
        chooseFundingWallet,
        // Step 2 — recipients + delivery
        recipients,
        setRecipients,
        addRecipient,
        importRecipients,
        notificationMode,
        setNotificationMode,
        scheduleMode,
        setScheduleMode,
        scheduleDate,
        setScheduleDate,
        scheduleTime,
        setScheduleTime,
        scheduleTz,
        setScheduleTz,
        expiryMode,
        setExpiryMode,
        customExpiryDate,
        setCustomExpiryDate,
        // Step 2 — manual gift links
        linkToList,
        setLinkToList,
        linkQuantity,
        setLinkQuantity,
        senderFirstName,
        setSenderFirstName,
        senderLastName,
        setSenderLastName,
        senderCompany,
        setSenderCompany,
        senderSignature,
        setSenderSignature,
        activationMode,
        setActivationMode,
        activationDate,
        setActivationDate,
        activationTime,
        setActivationTime,
        activationTz,
        setActivationTz,
        universalAccepted,
        universalAcceptedAt,
        acceptUniversal,
        universalToken: draft.universalToken,
        step2Complete,
        // Step 3 — design
        stage3Email,
        setStage3Email,
        stage3Unboxing,
        setStage3Unboxing,
        stage3Landing,
        setStage3Landing,
        step3Complete,
        designActiveTab,
        setDesignActiveTab,
        designDrawerOpen,
        setDesignDrawerOpen,
        fullscreenPreviewOpen,
        setFullscreenPreviewOpen,
        emailConfigTab,
        setEmailConfigTab,
        landingConfigTab,
        setLandingConfigTab,
        reminderCount: permittedReminderCount,
        setReminderCount,
        reminderEmails,
        setReminderEmail,
        recipientsAccordions,
        toggleRecipientsAccordion,
        openRecipientsAccordion,
        remindersOpen,
        setRemindersOpen,
        cropDialogTarget,
        openCropDialog,
        closeCropDialog,
        sendTestOpen,
        openSendTest,
        closeSendTest,
        toastMessage,
        showToast,
        clearToast,
        // Step 4 — review & checkout
        selectedCauses,
        setSelectedCauses,
        rolloverEnabled,
        setRolloverEnabled,
        applyAllCredits,
        setApplyAllCredits,
        payAsYouGive,
        setPayAsYouGive,
        postpaid,
        setPostpaid,
        submitCampaign,
        validationIssues,
        showValidation,
        clearValidation,
      }}
    >
      {children}
    </CampaignBuilderContext.Provider>
  );
}

export function useCampaignBuilder() {
  const ctx = useContext(CampaignBuilderContext);
  if (!ctx) throw new Error("useCampaignBuilder must be used within CampaignBuilderProvider");
  return ctx;
}
